import { API_URL } from "env";
import { Activity, CircleX, DollarSign, Eye, Mail, Send, Share2, TrendingUp, Users } from "lucide-react";
import {
  PendingInviteApi,
  PendingInvites,
  RecentTeamItem,
  StatItemTeamDetail,
  StatsTeams,
  Team,
  TeamApi,
  TeamMember,
  TeamsList,
  TeamsListItem,
  MyTradeAccounts,
  mapPendingInvite,
  TradeAccounts,
  Stat,
  TeamDetailStats,
  QRInviteResponse,
  TeamDetailChartDataApi,
  TeamDetailMetricsApi,
  TeamDetailOverviewApi,
  TeamDetailTrackingTableApi,
  MyJoinRequest,
} from "./type";
import { toast } from "react-toastify";
import { t } from "i18next";

/**
 * Creates a new team by sending a POST request to the backend teams endpoint.
 *
 * Sends a JSON payload `{ name }` to `${API_URL}/api/v1/be/teams` with an
 * Authorization header containing a bearer token retrieved from `localStorage`.
 *
 * @param name - The name of the team to create. Should be a non-empty string.
 * @returns A Promise that resolves with the parsed JSON response from the server (the created team object).
 * @throws {Error} If the HTTP response has a non-OK status, an Error with message "Failed to create team" is thrown.
 * @throws {TypeError|SyntaxError} If the network request fails or the response cannot be parsed as JSON, the underlying error will be propagated.
 * @remarks
 * - Side effects: reads `localStorage.getItem('token')` and performs a network request.
 * - Request headers: `Content-Type: application/json`, `Authorization: Bearer <token>`.
 *
 * @example
 * const team = await createNewTeam('My New Team');
 *
 * if (success) {
 *   // handle successful rejection
 * } else {
 *   // handle failure
 * }
 */

async function createNewTeam(name: string) {
  const res = await fetch(API_URL + `/api/v1/be/create-team`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) {
    toast.error("Failed to create team");
    throw new Error("Failed to create team");
  }
  const data = await res.json();
  return data;
}


/**
 * Fetches the current user's teams from the backend API.
 *
 * This function reads the user ID from localStorage (expects a JSON object under the "user" key)
 * and uses the token stored under the "token" key to authorize the request. It performs a GET
 * request to `${API_URL}/api/v1/be/my-teams` and returns the `teams` field from the JSON response.
 *
 * @async
 * @returns {Promise<any[]>} A promise that resolves to the array of teams returned by the API.
 *
 * @throws {Error} If no user ID is found in localStorage ("User not logged in").
 * @throws {Error} If the fetch response is not ok ("Failed to fetch teams").
 *
 * @remarks
 * - Requires a global `API_URL` constant to be defined.
 * - Expects localStorage to contain:
 *    - "user": a JSON stringified object that includes an `id` property.
 *    - "token": a string JWT or bearer token for authorization.
 *
 * @example
 * const teams = await getMyTeam();
 *
 * if (success) {
 *   // handle successful rejection
 * } else {
 *   // handle failure
 * }
 */
async function getMyTeam(): Promise<TeamApi[]> {
  const userId = JSON.parse(localStorage.getItem("user") || "{}").id;
  if (!userId) {
    throw new Error("User not logged in");
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 30000); // 30s timeout

  try {
    const res = await fetch(API_URL + `/api/v1/be/my-teams`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      signal: controller.signal,
    });

    if (!res.ok) {
      toast.error("Failed to fetch teams");
      throw new Error("Failed to fetch teams");
    }

    const data = await res.json();
    return data.teams;
  } catch (err: any) {
    if (err.name === "AbortError") {
      toast.error("Request timed out");
      throw new Error("Request timed out");
    }
    toast.error("Failed to fetch teams");
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetches detailed information for a team by its identifier.
 *
 * Sends a GET request to `${API_URL}/api/v1/be/teams/{teamId}` with a JSON
 * content type and an Authorization header using the bearer token stored
 * in localStorage under the key "token".
 *
 * @param teamId - The numeric identifier of the team to fetch.
 * @returns A Promise that resolves to the parsed JSON response containing the team details.
 *          The exact shape of the returned object depends on the backend API.
 * @throws {Error} If the response has a non-OK HTTP status or the fetch fails.
 *
 * @remarks
 * - Relies on a global `API_URL` value for the request base URL.
 * - If no token exists in localStorage, an empty string is used in the Authorization header.
 * - Callers should validate the returned data shape and handle errors (e.g., using try/catch).
 *
 * @example
 * try {
 *   const team = await getTeamDetail(42);
 *   console.log(team);
 * } catch (error) {
 *   console.error("Failed to load team details:", error);
 * }
 */
async function getTeamDetail(teamId: number): Promise<Team> {
  const res = await fetch(API_URL + `/api/v1/be/teams/${teamId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
  });
  if (!res.ok) {
    toast.error("Failed to fetch team detail");
    throw new Error("Failed to fetch team detail");
  }
  const data = await res.json();
  return data;
}

/**
 * Converts an array of TeamApi objects into a TeamsList suitable for UI consumption.
 *
 * For each TeamApi item:
 * - Builds a member summary string like "3 members" (pluralized).
 * - Uses the joined_at of the first member to produce createdDate formatted with toLocaleDateString("en-GB") (DD/MM/YYYY). If there are no members, createdDate defaults to the current date.
 * - Reads the current user id from localStorage.getItem("user") (expects a JSON object with an `id` field) and determines isLeader by checking whether the matching member has role === "Admin".
 * - Populates a TeamsListItem with fields:
 *   - id: team.team_id
 *   - name: team.team_name
 *   - description: `Total Balance: ${team.total_balance}`
 *   - members: summary text
 *   - createdDate: formatted date string
 *   - isLeader: boolean
 *
 * @param data - Array of TeamApi objects to transform.
 * @returns Promise<TeamsList> resolving to an array of TeamsListItem objects mapped from the input.
 *
 * @throws {SyntaxError} If the stored "user" value is not valid JSON.
 * @throws {TypeError} If expected properties (e.g., members, team_id, team_name, total_balance) are missing or of an unexpected type on the input objects.
 *
 * @remarks
 * - The function performs a synchronous mapping and returns the result wrapped in a Promise.
 * - The leadership check relies on the literal string "Admin"; change this if your backend uses a different role identifier.
 *
 * @example
 * // const list = await MyTeamList(apiResponse);
 */

async function MyTeamList(data: TeamApi[]): Promise<TeamsList> {

  // Map từ TeamApi sang TeamsListItem
  const teamsList: TeamsList = data.map((team: TeamApi): TeamsListItem => {
    // Tạo summary cho members (ví dụ: "3 members")
    const membersCount = team.members.length;

    // Format ngày tạo (có thể lấy từ joined_at của member đầu tiên hoặc mặc định)
    const createdDate =
      team.members.length > 0
        ? new Date(team.members[0].joined_at).toLocaleDateString("en-GB") // DD/MM/YYYY
        : new Date().toLocaleDateString("en-GB");

    // Kiểm tra xem user hiện tại có phải là leader không
    const currentUserId = JSON.parse(localStorage.getItem("user") || "{}").id;
    const currentMember = team.members.find(
      (member) => member.user_id === currentUserId
    );
    const isLeader = currentMember?.role === "Admin";
    const status = currentMember?.status || "Active";

    return {
      id: team.team_id,
      name: team.team_name,
      description: `Total Balance: ${team.total_balance}`,
      total_balance: Number(team.total_balance) || 0,
      members: membersCount,
      createdDate: createdDate,
      isLeader: isLeader,
      role: currentMember?.role,
      total_trade_account: team.total_trade_account,
      status: status,
    };
  });

  return teamsList;
}

/**
 * Sends an invitation to join a team by email.
 *
 * Performs a POST request to the endpoint `${API_URL}/api/v1/be/teams/{teamId}/invite` with a JSON
 * body containing the invitee's email. The request includes a "Content-Type: application/json"
 * header and an "Authorization" header that uses the token stored in localStorage under the key
 * "token".
 *
 * @param teamId - The numeric identifier of the team to which the invite will be sent.
 * @param inviteEmail - The email address to invite.
 * @returns A Promise that resolves to the parsed JSON response from the API. The exact shape of the
 * returned data depends on the backend implementation.
 * @throws {Error} Throws an Error with the message "Failed to send team invite" if the network
 * request fails or the response has a non-OK status.
 *
 * @example
 * // Ensure API_URL is defined and a valid token is stored in localStorage:
 * // localStorage.setItem('token', 'your-jwt-token');
 * // sendTeamInvite(42, 'invitee@example.com').then(...).catch(...);
 */
async function sendTeamInvite(teamId: number, inviteEmail: string) {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${teamId}/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({ email: inviteEmail }),
    });
    if (!res.ok) {
      toast.error(t('Teams_dashboard.team_invite.error'));
      throw new Error("Failed to send team invite");
    }
    const data = await res.json();
    toast.success(t('Teams_dashboard.team_invite.success'));
    return data;
  } catch {
    toast.error(t('Teams_dashboard.team_invite.error'));
    throw new Error("Failed to send team invite");
  }
}


/**
 * Fetches pending invites for the current user from the backend and returns them as a mapped
 * PendingInvites value.
 *
 * Performs a GET request to `${API_URL}/api/v1/be/my-invites` and includes an Authorization
 * header using the token stored in localStorage under the "token" key. On success, the response
 * JSON is expected to contain a `pending_invites` array which is mapped from the API shape
 * (PendingInviteApi) to the local shape via `mapPendingInvite`.
 *
 * Side effects:
 * - Reads the global `API_URL`.
 * - Reads the `token` from `localStorage`.
 * - Calls the global `fetch` API.
 *
 * Error handling:
 * - If the HTTP response has a non-ok status, the function throws `Error("Failed to fetch pending invites")`.
 * - Network failures or JSON parse/mapping errors will also result in an Error being thrown with the
 *   same message.
 *
 * Example:
 * ```
 * try {
 *   const invites = await getPendingInvites();
 *   // handle invites
 * } catch (err) {
 *   // handle error
 * }
 * ```
 *
 * @returns Promise<PendingInvites> A promise that resolves to the list of pending invites mapped to the local type.
 * @throws Error When the request fails, the response is not ok, or parsing/mapping of the response fails.
 * @see mapPendingInvite
 * @remarks Expects the backend response JSON to include a `pending_invites` array of PendingInviteApi objects.
 */
async function getPendingInvites(): Promise<PendingInvites> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/my-invites?action=wait`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to fetch pending invites");
    }
    const data = await res.json();

    // Map từ API response sang PendingInvites
    const pendingInvites: PendingInvites = data.data.map(
      (invite: PendingInviteApi) => mapPendingInvite(invite)
    );

    return pendingInvites;
  } catch {
    toast.error("Failed to fetch pending invites");
    throw new Error("Failed to fetch pending invites");
  }
}

/**
 * Attempts to accept a team invite by POSTing to the invites respond endpoint.
 *
 * Sends a JSON body `{ action: "accept" }` and includes an Authorization header
 * with the token read from `localStorage.getItem("token")`.
 * Resolves to `true` when the HTTP response has an OK status (`res.ok`), otherwise
 * resolves to `false`. Network or other exceptions are caught and result in `false`.
 *
 * @param teamId - Numeric identifier of the team/invite to respond to.
 * @returns A Promise that resolves to `true` if the invite was accepted successfully (HTTP 2xx), or `false` on any failure.
 *
 * @example
 * const accepted = await acceptTeamInvite(42);
 * if (accepted) {
 *   // invite accepted
 * } else {
 *   // acceptance failed
 * }
 *
 * @remarks
 * - Requires `API_URL` to be defined in the enclosing module scope.
 * - Token is retrieved from localStorage using the key `"token"`.
 * - This function intentionally does not throw; callers should check the boolean result.
 */
async function acceptTeamInvite(teamId: number, type: string, user_id?: number): Promise<boolean> {
  try {
    if (type === "email") {
      const res = await fetch(API_URL + `/api/v1/be/invites/${teamId}/respond`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ action: "accept" }),
      });
      if (!res.ok) {
        toast.error(t("Teams_dashboard.acceptTeamInvite.error"));
        return false;
      } else {
        toast.success(t("Teams_dashboard.acceptTeamInvite.success"));
        return true;
      }
    } else {
      const res = await fetch(API_URL + `/api/v1/be/teams/${teamId}/requests/${user_id}/handle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ action: "approve" }),
      });
      if (!res.ok) {
        toast.error(t("Teams_dashboard.acceptTeamInvite.error"));
        return false;
      } else {
        toast.success(t("Teams_dashboard.acceptTeamInvite.success"));
        return true;  
      }
    }


  } catch {
    toast.error(t("Teams_dashboard.acceptTeamInvite.error"));
    return false;
  }
}

/**
 * Sends a rejection for a team invite to the backend.
 *
 * Performs a POST request to `/api/v1/be/invites/{teamId}/respond` with a JSON
 * payload of `{ action: "reject" }`. The request includes an `Authorization`
 * header containing a bearer token read from `localStorage.getItem("token")`.
 *
 * @param teamId - The numeric identifier of the team invite to reject.
 * @returns A Promise that resolves to `true` if the HTTP response has a successful
 *          status (`res.ok`), or `false` if the response is not ok or if an error
 *          occurs (network failure, exception, etc.).
 *
 * @remarks
 * - If no token exists in localStorage, an empty string is sent in the Authorization header.
 * - Network errors are caught internally; this function never throws and instead resolves to `false`.
 * - The function performs an external network call and may be subject to CORS, latency, and server-side errors.
 *
 * @example
 * // Reject invite for team with ID 42
 * const success = await rejectTeamInvite(42);
 * if (success) {
 *   // handle successful rejection
 * } else {
 *   // handle failure
 * }
 */
async function rejectTeamInvite(teamId: number, type: string, user_id?: number): Promise<boolean> {
  try {
    if (type === "email") {
      const res = await fetch(API_URL + `/api/v1/be/invites/${teamId}/respond`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ action: "reject" }),
      });
      if (!res.ok) {
        toast.error(t("Teams_dashboard.declineTeamInvite.error"));
        return false;
      } else {
        toast.success(t("Teams_dashboard.declineTeamInvite.success"));
        return true;
      }
    } else {
      const res = await fetch(API_URL + `/api/v1/be/teams/${teamId}/requests/${user_id}/handle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ action: "reject" }),
      });
      if (!res.ok) {
        toast.error(t("Teams_dashboard.declineTeamInvite.error"));
        return false;
      } else {
        toast.success(t("Teams_dashboard.declineTeamInvite.success"));
        return true;
      }
    }
  } catch {
    toast.error(t("Teams_dashboard.declineTeamInvite.error"));
    return false;
  }
}


/**
 * Adds a trade account to a team by calling the backend share-account endpoint.
 *
 * Sends a POST request to `${API_URL}/api/v1/be/teams/share-account` with a JSON body:
 * `{ team_id, account_number }`. The request uses "Content-Type: application/json" and
 * includes a Bearer token obtained from `localStorage.getItem("token")`.
 *
 * @param team_id - The numeric ID of the team to which the trade account will be added.
 * @param trade_account_number - The trade account number to share with the team.
 * @returns A Promise that resolves to the parsed JSON response from the server on success.
 *          The exact shape of the returned data depends on the backend API.
 * @throws Will throw an Error with message "Failed to add team trade account" if the fetch
 *         request fails or the response has a non-OK status.
 * @async
 * @example
 * // Add account "ACC-123" to team with ID 42
 * const response = await addTeamTradeAccount(42, "ACC-123");
 */

async function addTeamTradeAccount(team_id: number, trade_account_number: string) {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/account/share-account`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        "Accept": "application/json",
      },
      body: JSON.stringify({
        team_id: team_id,
        account_number: trade_account_number
      }),
    });

    const data = await res.json();

    // Nếu lỗi (bao gồm cả lỗi 422), ta trả về data kèm theo một flag `isError: true` 
    // để hàm handle bên ngoài dễ phân biệt.
    if (!res.ok) {
      return { ...data, isError: true };
    }

    return data;
  }
  catch {
    toast.error(t("Teams_dashboard.addTradeAccountToTeam.error"));
    throw new Error("Failed to add team trade account");
  }
}

async function removeTeamTradeAccount(team_id: number, account_number: string) {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/accounts/${account_number}/remove`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        "Content-Type": "application/json",
      },
    });
    return res;
  }
  catch {
    toast.error(t("Teams_dashboard.removeTradeAccountFromTeam.error"));
    throw new Error("Failed to remove team trade account");
  }
}


/**
 * Fetches the current user's trade accounts from the backend API.
 *
 * The function issues a GET request to `${API_URL}/api/v1/be/teams/account` and expects a JSON
 * response containing a `trade_account` property. An Authorization bearer token is read from
 * `localStorage.getItem("token")` and sent in the request headers.
 *
 * Behavior:
 * - On HTTP success (res.ok), resolves with the value of `data.trade_account`.
 * - On non-OK HTTP responses or any error during fetch/JSON parsing, displays an error toast
 *   (`toast.error("Failed to fetch user trade accounts")`) and rejects by throwing an Error.
 *
 * @async
 * @returns {Promise<MyTradeAccounts>} A promise that resolves to the user's trade accounts.
 * @throws {Error} Thrown when the network request fails, the response is not OK, or response parsing fails.
 * @remarks Consumers should ensure a valid token is present in localStorage before calling,
 * and handle the thrown Error (e.g., with try/catch) as the function surfaces failures by throwing.
 */
async function getUserTradeAccounts(): Promise<MyTradeAccounts> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/account`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      toast.error("Failed to fetch user trade accounts");
      throw new Error("Failed to fetch user trade accounts");
    }
    const data = await res.json();
    return data.trade_account;
  }
  catch {
    toast.error("Failed to fetch user trade accounts");
    throw new Error("Failed to fetch user trade accounts");
  }
}


/**
 * Fetches team statistics from the backend API.
 *
 * Sends a GET request to `${API_URL}/api/v1/be/teams/stats` with an Authorization header
 *  
 * * containing a bearer token from `localStorage.getItem("token")`. On success, resolves with the parsed JSON response. On failure (non-OK response or network error), displays an error toast and throws an Error.  
 * @returns A Promise that resolves to the team statistics data returned by the API.
 * @throws {Error} If the HTTP response is not OK or if a network error occurs, an Error with message "Failed to fetch team stats" is thrown.
 * @remarks Consumers should handle the thrown Error (e.g., with try/catch) as this function surfaces failures by throwing.
 */

async function getTeamStats(): Promise<TeamDetailStats> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/stats`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to fetch team stats");
    }
    const data = await res.json();
    return data;
  }
  catch {
    toast.error("Failed to fetch team stats");
    throw new Error("Failed to fetch team stats");
  }
}


async function changeRole(team_id: number, user_id: number, new_role: "Deputy" | "Member") {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/roles/${user_id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({
        role: new_role
      }),
    });
    if (!res.ok) {
      throw new Error("Failed to change member role");
    }
    const data = await res.json();
    return data;
  }
  catch {
    toast.error("Failed to change member role");
    throw new Error("Failed to change member role");
  }
}


async function kickMember(team_id: number, user_id: number) {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/kick/${user_id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to kick member");
    }
    const data = await res.json();
    return data;
  }
  catch {
    toast.error("Failed to kick member");
    throw new Error("Failed to kick member");
  }
}

async function leaveTeam(team_id: number) {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/leave`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to leave team");
    }
    const data = await res.json();
    return data;
  }
  catch {
    toast.error("Failed to leave team");
    throw new Error("Failed to leave team");
  }
}

async function generateInviteQR(team_id: number): Promise<QRInviteResponse> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/generate-link`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to generate invite QR code");
    }
    const data = await res.json();
    return data;
  }
  catch {
    // The share modal shows its own error + retry state.
    throw new Error("Failed to generate invite QR code");
  }
}

async function addToTeamByQR(signature: string, expires: string, team_id: number) {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/request-to-join`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({
        signature,
        expires
      }),
    });
    const data = await res.json();
    return data;
  }
  catch {
    toast.error("Failed to join team by QR code");
    throw new Error("Failed to join team by QR code");
  }
}

async function getTeamPendingRequests(team_id: number) {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/requests`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to fetch team pending requests");
    }
    const data = await res.json();
    return data.pending_requests;
  }
  catch {
    toast.error("Failed to fetch team pending requests");
    throw new Error("Failed to fetch team pending requests");
  }
}

async function handleTeamJoinRequest(team_id: number, user_id: number, action: "approve" | "reject") {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/requests/${user_id}/handle`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({
        action
      }),
    });
    if (!res.ok) {
      throw new Error(`Failed to ${action} team join request`);
    }
    const data = await res.json();
    return data;
  }
  catch {
    throw new Error(`Failed to ${action} team join request`);
  }
}

async function getTeamDetailOverview(team_id: number): Promise<TeamDetailOverviewApi> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/dashboard/overview`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("API request failed with status " + res.status);
    }
    const data = await res.json();
    return data;
  }
  catch (error) {
    toast.error("An error occurred while fetching team detail overview");
    throw new Error("Failed to fetch team detail overview");
  }
}

async function getTeamDetailMetrics(team_id: number): Promise<TeamDetailMetricsApi> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/dashboard/metrics`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("API request failed with status " + res.status);
    }
    const data = await res.json();
    return data;
  }
  catch (error) {
    toast.error("An error occurred while fetching team detail metrics");
    throw new Error("Failed to fetch team detail metrics");
  }
}

async function getTeamDetailCharts(team_id: number): Promise<TeamDetailChartDataApi> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/teams/${team_id}/dashboard/charts`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("API request failed with status " + res.status);
    }
    const data = await res.json();
    if (data.data && data.data.length === 0) {
      return {
        success: true,
        message: "No data available",
        data: {
          treemap: {
            series: [],
            total_size: 0,
          },
          distribution: {
            phases: {
              funded: 0,
              challenge: 0,
              verification: 0,
              newbie: 0,
              total: 0,
            },
            trades: {
              win: 0,
              loss: 0,
              breakeven: 0,
              total: 0,
            },
          },
          member_charts: {
            losing_trades: {
              account_numbers: [],
              first_name: [],
              last_name: [],
              data: [],
            },
            pass_probability: {
              account_numbers: [],
              first_name: [],
              last_name: [],
              data: [],
            },
            violations: {
              account_numbers: [],
              first_name: [],
              last_name: [],
              no_sl: [],
              missing_journal: [],
            },
          }
        },
      };
    }
    return data;
  }
  catch (error) {
    toast.error("An error occurred while fetching team detail charts");
    throw new Error("Failed to fetch team detail charts");
  }
}

async function getTeamDetailTrackingTable(team_id: number, start_date?: string, end_date?: string): Promise<TeamDetailTrackingTableApi> {
  try {
    let url = API_URL + `/api/v1/be/teams/${team_id}/dashboard/tracking-table`;
    if (start_date === "all" || !start_date) {
      url += "?start_date=all";
    } else {
      url += `?start_date=${start_date}`;
      if (end_date) {
        url += `&end_date=${end_date}`;
      }
    }
    // The endpoint silently pages at 10 rows, which hid half of the team's accounts.
    url += "&per_page=500";
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("API request failed with status " + res.status);
    }
    const data = await res.json();
    return data;
  }
  catch (error) {
    toast.error("An error occurred while fetching team detail tracking table");
    throw new Error("Failed to fetch team detail tracking table");
  }
}

/**
 * Mock function to fetch join requests sent by the current user.
 * In the future, this will call a real API endpoint like `/api/v1/be/my-join-requests`.
 */
async function getMyTeamRequests(): Promise<PendingInvites> {
  try {
    const res = await fetch(API_URL + `/api/v1/be/my-invites`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      },
    });
    if (!res.ok) {
      throw new Error("Failed to fetch pending invites");
    }
    const data = await res.json();

    // Map từ API response sang PendingInvites
    const pendingInvites: PendingInvites = data.data.map(
      (invite: PendingInviteApi) => mapPendingInvite(invite)
    );

    return pendingInvites;
  } catch {
    toast.error("Failed to fetch pending invites");
    throw new Error("Failed to fetch pending invites");
  }
}

/**
 * 
 * 
 * 
 * 
 * 
 * 
 *    *       Interface representing team statistics.
 * 
 * 
 * 
 * 
 * 
 * 
 * 
 */








/**
 * Converts a TeamsList into an array of RecentTeamItem objects.
 *
 * For each team, the function:
 * - Extracts the leading numeric value from `team.members` by splitting on space and parsing with base 10.
 * - Falls back to 0 when parsing fails or no numeric prefix exists.
 * - Maps `team.isLeader` to a status string: `"Leader"` when true, otherwise `"Member"`.
 *
 * @param teams - The list of teams to convert (TeamsList).
 * @returns An array of RecentTeamItem objects with `id`, `name`, numeric `members`, and `status` ("Leader" | "Member").
 *
 * @remarks
 * Expects `team.members` to contain a numeric count optionally followed by text (e.g., "3 members").
 * Non-numeric or missing counts will be treated as 0.
 */
function ConvertTeamListToRecentTeamItems(teams: TeamsList): RecentTeamItem[] {
  return teams.map((team) => {
    return {
      id: team.id,
      name: team.name,
      members: team.members,
      status: team.role || (team.isLeader ? "Admin" : "Member"),
    };
  });
}

/**
 * Converts a Team object's members into an array of pending invite objects.
 *
 * Filters the provided team's members for those with `invitation_status === "Pending"`,
 * and maps each pending member to a normalized invite object:
 *  - `name` is taken from `member.user_name` or falls back to `"Unknown"`.
 *  - `email` is taken from `member.accountNumber` or falls back to an empty string.
 *  - `status` is the literal string `'pending'`.
 *
 * @param detail - The Team object to read members from. Each member is expected to have
 *                 at least the properties `invitation_status` (string), and optionally
 *                 `user_name` and `accountNumber` (strings).
 * @returns An array of pending invite objects: { name: string; email: string; status: 'pending' }.
 *          If no members are pending, an empty array is returned.
 * @throws {Error} Throws an Error with message "Failed to convert team detail to pending invites"
 *         if an unexpected error occurs while processing the team detail.
 *
 * @example
 * // returns [{ name: "Alice", email: "alice@example.com", status: "pending" }]
 * CovertTeamDetailToPendingInvites(team);
 * 
 * 
 * @remarks
 * Use this helper to produce consistent, display-ready metrics for a teams dashboard.
 */
function CovertTeamDetailToPendingInvites(detail: Team) {
  try {
    const pendingInvites = detail.members.filter((member) => member.invitation_status === "Pending" || member.invitation_status === "Declined").map((member) => ({
      name: member.user_name || "Unknown",
      email: member.email || "",
      status: member.invitation_status || "Unknown",
    }));
    return pendingInvites;
  }
  catch (error) {
    throw new Error("Failed to convert team detail to pending invites");
  }
}

/**
 * ConvertTeamDetailToStats
 *
 * Convert a Team detail object into an array of StatItemTeamDetail objects suitable for rendering
 * as summary statistics in a UI.
 *
 * Behavior:
 * - Reads `detail.total_balance` and converts it to a number via `parseFloat`. If `total_balance`
 *   is falsy, it defaults to 0. The "Total Balance" stat's `value` is returned as a formatted
 *   currency string prefixed with `$` and fixed to two decimal places (e.g. "$123.45").
 * - Counts members whose `invitation_status` is exactly `"Accepted"` and returns that number as
 *   the "Total Members" stat.
 * - Counts members whose `invitation_status` is exactly `"Pending"` and returns that number as
 *   the "Pending Invites" stat.
 * - Uses the length of `detail.trade_accounts` for the "Trading Accounts" stat.
 * - Each stat item includes a `title`, `value`, an `icon` (JSX.ReactElement), and `textColor`
 *   (hex color string) suitable for visual display.
 *
 * Notes:
 * - The function is pure and has no side effects; it only reads the provided `detail` object and
 *   returns a new array of stat items.
 * - The "Total Balance" value is a formatted string while the other stats are numeric values.
 *
 * @param detail - Team detail object to convert. Expected shape includes:
 *   - total_balance?: string | number
 *   - members: Array<{ invitation_status?: string }>
 *   - trade_accounts: Array<any>
 *
 * @returns An array of StatItemTeamDetail representing:
 *   - "Total Members": number of accepted members
 *   - "Pending Invites": number of pending invitations
 *   - "Trading Accounts": number of trade accounts
 *   - "Total Balance": formatted currency string
 *
 * @throws {Error} If any unexpected error occurs during conversion the function will throw
 *   an Error with message "Failed to convert team detail to stats".
 *
 * @example
 * // const stats = ConvertTeamDetailToStats(teamDetail);
 * // stats[0].title === "Total Members"
 * // stats[0].value -> number of accepted members
 * 
 * @remarks
 * Use this helper to produce consistent, display-ready metrics for a teams dashboard.
 */
function ConvertTeamDetailToStats(detail: Team): StatItemTeamDetail[] {
  try {
    const total_balance = detail.total_balance ? parseFloat(detail.total_balance) : 0;
    const total_members = detail.members.filter((member) => member.invitation_status === "Accepted").length;
    const total_pending_invites = detail.members.filter((member) => member.invitation_status === "Pending").length;
    const total_trade_accounts = detail.trade_accounts.length;

    const stats = [
      {
        title: "Total Members",
        value: total_members,
        icon: <Users color="#2563eb" />,
        textColor: "#2563eb",
      },
      {
        title: "Pending Invites",
        value: total_pending_invites,
        icon: <Mail color="#ca8a04" />,
        textColor: "#ca8a04",
      },
      {
        title: "Trading Accounts",
        value: total_trade_accounts,
        icon: <Share2 color="#16a361" />,
        textColor: "#16a361",
      },
      {
        title: "Total Balance",
        value: `$${total_balance.toFixed(2)}`,
        icon: <Share2 color="#9333ea" />,
        textColor: "#9333ea",
      },
    ];

    return stats;


  }
  catch (error) {
    throw new Error("Failed to convert team detail to stats");
  }
}

/**
 * Extracts the trade accounts list from a Team detail object.
 *
 * This function returns the value of the `trade_accounts` property from the
 * provided `Team` object as a `TradeAccounts` value. The returned value is
 * returned as-is (no deep copy is performed).
 *
 * @param detail - The Team object containing trade account information.
 * @returns The `TradeAccounts` extracted from the given team detail.
 * @throws {Error} Throws an Error with message "Failed to convert team detail to trade account list"
 * if the extraction fails (for example, if `detail` is `null`/`undefined` or the property access throws).
 */
function ConvertTeamDetailToTradeAccountList(detail: Team): TradeAccounts {
  try {
    return detail.trade_accounts;
  }
  catch (error) {
    throw new Error("Failed to convert team detail to trade account list");
  }
}
/**
 * Convert an array of TeamApi entries into an array of Stat objects used for UI display.
 *
 * The function computes four statistics:
 *  - "Total Teams": the number of teams in the input array.
 *  - "Team Members": the total accepted/active members across all teams. If a team does not
 *    include invitation statuses on its members, the fallback is to count all members for that team.
 *    The returned value for this stat is a string which includes the total accepted members and,
 *    if any, the count of pending invites in the format: "<accepted> + (<pending> pending)".
 *  - "Trading Accounts": the total number of trading accounts across all teams. The function
 *    supports team.total_trade_account being either an array (counts its length) or a numeric/string
 *    value (coerced to Number). Non-numeric values are treated as zero.
 *  - "Total Balance": the sum of balances across all teams. The function looks for either
 *    team.total_balance or team.totalBalance, coerces the value to Number and ignores NaN values.
 *    The returned value is formatted as a USD-style string with two decimal places (e.g. "$123.45").
 *
 * Implementation notes:
 *  - The function tolerates missing fields: members, total_trade_account, total_balance/totalBalance.
 *  - It prefers explicit invitation/status semantics when available: members with status "Active"
 *    or "Accepted" are counted as accepted; members with status "Invited" are counted as pending.
 *  - Some fields are accessed via `any` casts to support inconsistent payload shapes.
 *  - Icons and background colors are included in each Stat for UI rendering (JSX values).
 *
 * @param myTeams - Array of TeamApi objects to aggregate into dashboard statistics.
 *                  Each TeamApi may optionally include:
 *                    - members?: Array<{ status?: string }>
 *                    - total_trade_account?: number | string | any[]
 *                    - total_balance?: number | string
 *                    - totalBalance?: number | string
 *
 * @returns An array of four Stat objects ready for UI consumption:
 *          [
 *            { title: "Total Teams", value: number, icon: JSX.Element, bgColor: string },
 *            { title: "Team Members", value: string, icon: JSX.Element, bgColor: string },
 *            { title: "Trading Accounts", value: number, icon: JSX.Element, bgColor: string },
 *            { title: "Total Balance", value: string, icon: JSX.Element, bgColor: string }
 *          ]
 *
 * @throws Error If an unexpected runtime error occurs while computing the statistics.
 *
 * @example
 * // const stats = ConvertMyTeamsToStats(myTeams);
 * // stats[0].value -> total teams (number)
 * // stats[1].value -> "12 + (2 pending)" or "12" depending on pending invites
 *
 * @remarks
 * Use this helper to produce consistent, display-ready metrics for a teams dashboard.
 */
function ConvertMyTeamsToStats(myTeams: TeamApi[]): Stat[] {
  try {
    const total_teams = myTeams.length;

    const total_members = myTeams.reduce((acc, team) => {
      const members = team.members || [];
      // Prefer counting accepted members, fallback to all members if no invitation_status
      const acceptedCount = members.filter(
        (m) => m.status === "Active" || m.status === "Accepted"
      ).length;
      return acc + (acceptedCount || members.length || 0);
    }, 0);
    const total_pending_invites = myTeams.reduce((acc, team) => {
      const members = team.members || [];
      const pendingCount = members.filter(
        (m) => m.status === "Invited"
      ).length;
      return acc + pendingCount;
    }, 0);

    const total_trade_accounts = myTeams.reduce((acc, team) => {
      const accountsField = (team as any).total_trade_account;
      let accountsCount = 0;
      if (Array.isArray(accountsField)) {
        accountsCount = accountsField.length;
      } else {
        accountsCount = Number(accountsField) || 0;
      }
      return acc + accountsCount;
    }, 0);

    const total_balance = myTeams.reduce((acc, team) => {
      const b = Number((team as any).total_balance ?? (team as any).totalBalance ?? 0);
      return acc + (isNaN(b) ? 0 : b);
    }, 0);

    const stats: Stat[] = [
      {
        title: "total_teams",
        value: total_teams,
        icon: <Users color="#2563eb" />,
        bgColor: "#dbeafe",
      },
      {
        title: "team_members",
        value: `${total_members} ${total_pending_invites > 0 ? `+ (${total_pending_invites} pending)` : ""}`,
        icon: <Activity color="#16a34a" />,
        bgColor: "#dcfce7",
      },
      {
        title: "trading_accounts",
        value: total_trade_accounts,
        icon: <TrendingUp color="#9333ea" />,
        bgColor: "#f3e8ff",
      },
      {
        title: "Total Balance",
        value: `$${total_balance.toFixed(2)}`,
        icon: <DollarSign color="#ea580c" />,
        bgColor: "#ffedd5",
      },
    ];

    return stats;
  } catch (error) {
    throw new Error("Failed to convert my teams to team stats");
  }
}

/**
 * Determines whether the currently authenticated user is an admin of the provided team.
 *
 * The function reads the current user id from localStorage under the "user" key, looks up
 * that id among the team's members, and returns true if the matching member's role is "Admin".
 *
 * @param detail - The Team object containing a members array to check.
 * @returns `true` if the current user is found in `detail.members` with role `"Admin"`, otherwise `false`.
 * @throws {Error} Throws when the user id cannot be retrieved/parsed from localStorage or if the check cannot be completed.
 * @example
 * // localStorage.setItem('user', JSON.stringify({ id: 'user-123' }));
 * // const isAdmin = IsTeamAdmin(teamDetail);
 */
function IsTeamAdmin(detail: Team): boolean {
  try {
    const currentUserId = JSON.parse(localStorage.getItem("user") || "{}").id;
    const currentMember = detail.members.find(
      (member) => member.user_id === currentUserId
    );
    return currentMember?.role === "Admin" || currentMember?.role === "Deputy";
  }
  catch (error) {
    throw new Error("Failed to determine if user is team admin");
  }
}

function CheckDuplicateInviteEmail(member: TeamMember[], email: string): boolean {
  try {
    const duplicate = member.find(
      (m) => m.email === email && String(m.invitation_status || "").toLowerCase() === "accepted"
    );
    return !!duplicate;
  }
  catch (error) {
    throw new Error("Failed to check duplicate invite email");
  }
}



export {
  createNewTeam,
  getMyTeam,
  MyTeamList,
  getTeamDetail,
  sendTeamInvite,
  getPendingInvites,
  acceptTeamInvite,
  rejectTeamInvite,
  addTeamTradeAccount,
  removeTeamTradeAccount,
  getUserTradeAccounts,
  getTeamStats,
  changeRole,
  kickMember,
  leaveTeam,
  getTeamPendingRequests,
  getMyTeamRequests,
  handleTeamJoinRequest,
  generateInviteQR,
  addToTeamByQR,
  getTeamDetailOverview,
  getTeamDetailMetrics,
  getTeamDetailCharts,
  getTeamDetailTrackingTable,
  IsTeamAdmin,
  CheckDuplicateInviteEmail,
  CovertTeamDetailToPendingInvites,
  ConvertTeamListToRecentTeamItems,
  ConvertTeamDetailToStats,
  ConvertMyTeamsToStats,
  ConvertTeamDetailToTradeAccountList,
};
